import {
  DEDUPE_MS, findTapControl, judgeTap, NATIVE_CLICK_GRACE_MS, TAP_EVENT,
} from '~/utils/tapTolerance'

/**
 * Recover the taps the browser drops (decision 0014).
 *
 * A quarter of ordinary presses on the ET60 never become a click: the finger
 * rolls ~10 px while pressing, Chrome's slop is 8 px, and the tap is
 * reclassified as a scroll. This watches every press on a control and, when
 * the finger lifts close to where it landed and soon enough, activates the
 * control itself — but only if the browser has not delivered its own click
 * first. The rule lives in utils/tapTolerance.ts, where it is tested.
 *
 * Two shapes of loss, both covered. Early in the press the pointer is
 * CANCELLED (no pointerup ever comes), but the touch's own end still fires,
 * so the lift is read from touchend. Late in the press the pointer is
 * released normally and the click simply never follows; that lift is read
 * from pointerup. Either way the browser gets a short grace period to click,
 * and a trusted click that arrives late — after this has already acted — is
 * swallowed so nothing fires twice.
 *
 * Every recognised tap — touch, pen or mouse, recovered or not — is announced
 * as a `dockautomation:tap` event on the control at the moment of the lift.
 * The tap sound plays to that, which is what keeps a scroll that began on an
 * orders row, or a press dragged off a button, silent: neither is a tap.
 *
 * Left alone on purpose: a long press, a second finger, anything that is not
 * a control, and a control that is disabled or has since left the page. A
 * mouse press is judged and announced like any other but never recovered — a
 * mouse click is never lost. `window.__tapTolerance` counts what was
 * recovered, for a bench check over DevTools.
 */
export default defineNuxtPlugin(() => {
  type Press = { id: number, pointerType: string, control: HTMLElement, x: number, y: number, t: number, cancelled: boolean }
  const stats = { recovered: 0, deduped: 0 }
  ;(window as typeof window & { __tapTolerance?: typeof stats }).__tapTolerance = stats

  let press: Press | null = null
  let scheduled: { control: HTMLElement, timer: number } | null = null
  let synthetic: { control: HTMLElement, until: number } | null = null

  function clearScheduled() {
    if (scheduled) window.clearTimeout(scheduled.timer)
    scheduled = null
  }

  function fire(control: HTMLElement) {
    synthetic = { control, until: performance.now() + DEDUPE_MS }
    stats.recovered += 1
    // An untrusted click: Vue's @click handlers and Vue Router's link handler
    // both take it, and a native <a> falls through to navigation as usual.
    control.click()
  }

  function settle(x: number, y: number, t: number) {
    const p = press
    press = null
    if (!p) return
    if (judgeTap({ downX: p.x, downY: p.y, upX: x, upY: y, downT: p.t, upT: t }) !== 'activate') return
    if (!p.control.isConnected || findTapControl(p.control) === null) return
    // A tap, then — said so at the lift, before anyone clicks anything.
    p.control.dispatchEvent(new CustomEvent(TAP_EVENT, { bubbles: true, detail: { pointerType: p.pointerType } }))
    // A mouse click is never lost; only a touch needs the safety net below.
    if (p.pointerType === 'mouse') return
    // The browser's own click, when it comes, follows the lift within a few
    // milliseconds; it gets the grace period, and cancels this if it lands.
    clearScheduled()
    scheduled = {
      control: p.control,
      timer: window.setTimeout(() => { scheduled = null; fire(p.control) }, NATIVE_CLICK_GRACE_MS),
    }
  }

  document.addEventListener('pointerdown', (event) => {
    if (press && press.id !== event.pointerId) { press = null; return } // a second finger is not a tap
    if (event.pointerType === 'mouse' && event.button !== 0) { press = null; return } // right/middle: not a tap
    const control = findTapControl(event.target instanceof Element ? event.target : null)
    press = control instanceof HTMLElement
      ? { id: event.pointerId, pointerType: event.pointerType, control, x: event.clientX, y: event.clientY, t: event.timeStamp, cancelled: false }
      : null
  }, { capture: true, passive: true })

  document.addEventListener('pointerup', (event) => {
    if (press && event.pointerId === press.id) settle(event.clientX, event.clientY, event.timeStamp)
  }, { capture: true, passive: true })

  document.addEventListener('pointercancel', (event) => {
    // The browser took the pointer for a scroll. No pointerup will come, but
    // the touch still ends, and that end is where the finger lifted.
    if (press && event.pointerId === press.id) press.cancelled = true
  }, { capture: true, passive: true })

  document.addEventListener('touchend', (event) => {
    if (!press?.cancelled) return
    const touch = event.changedTouches[0]
    if (touch) settle(touch.clientX, touch.clientY, event.timeStamp)
  }, { capture: true, passive: true })

  document.addEventListener('touchcancel', () => { press = null }, { capture: true, passive: true })

  document.addEventListener('click', (event) => {
    if (!event.isTrusted) return // ours, or another script's — not the browser's tap
    const control = findTapControl(event.target instanceof Element ? event.target : null)
    if (scheduled && control === scheduled.control) {
      // The browser delivered after all; it wins, and nothing fires twice.
      clearScheduled()
      return
    }
    if (synthetic && control === synthetic.control && event.timeStamp <= synthetic.until) {
      // The late native twin of a tap already recovered.
      event.stopImmediatePropagation()
      event.preventDefault()
      stats.deduped += 1
      synthetic = null
    }
  }, { capture: true })
})
